import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import {
  getAllSpiceBlends,
  getAllPackagingVariants,
} from "../api/costingSettingsApi";
import { getBomEstimatedCost } from "../api/bomApi";
import { createRecipeSku } from "../api/sku";
import { generateId } from "../utils/generateId";

const FORMAT_OPTIONS = [
  { value: "1kg", label: "1 KG", weight: 1, isMasterCase: false },
  { value: "2.5kg", label: "2.5 KG", weight: 2.5, isMasterCase: false },
  { value: "5kg", label: "5 KG", weight: 5, isMasterCase: false },
  { value: "10kg", label: "10 KG", weight: 10, isMasterCase: false },
  {
    value: "master_case",
    label: "Master Case",
    weight: 25,
    isMasterCase: true,
  },
];

const emptyRow = () => ({
  id: generateId(),
  baseSku: "",
  nameEn: "",
  nameFr: "",
  baseDescriptionEn: "",
  baseDescriptionFr: "",
  formatType: "",
  isMasterCase: false,
  actualWeight: "",
  masterDefaultWeight: 25,
  generatedSku: "",
  upc: "",
  descriptionEn: "",
  descriptionFr: "",
  packagingOptions: [],
  selectedPackagingId: "",
  packagingName: "",
  packagingCost: 0,
  lineCost: 0,
  mfgCostPerKg: 0,
  lengthMm: "",
  widthMm: "",
  heightMm: "",
  lengthInch: "",
  widthInch: "",
  heightInch: "",
  ti: "",
  hi: "",
  new: "",
  category: "",
  countryOfOrigin: "",
  harmonizedTariff: "",
  fdaCode: "",
  expiryDays: "",
  manualDate: "",
  status: "",
  duplicateMessage: "",
  bomCost: "",
});

const normalizeWeight = (value) => {
  const num = Number(value);
  if (Number.isNaN(num) || num <= 0) return null;
  return Number(num.toFixed(3));
};

const packagingCost = (variant) =>
  (variant?.items || []).reduce(
    (sum, item) =>
      sum + (Number(item?.qty) || 0) * (Number(item?.unitCost) || 0),
    0,
  );

const normalizeDescriptionRoot = (value) =>
  String(value || "")
    .replace(/\(NEW\)/gi, "")
    .replace(/\bVRAC\b/gi, "")
    .replace(/\bNUTRIVILLA\b/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const formatCostValue = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return num.toFixed(4);
};

const defaultWeightFromFormat = (formatType) => {
  const format = FORMAT_OPTIONS.find((f) => f.value === formatType);
  return format?.weight || null;
};

const defaultPackagingForRow = (row, allPackaging) => {
  const sizeKey = row.isMasterCase
    ? "25kg"
    : row.formatType && row.formatType !== "master_case"
      ? row.formatType
      : null;

  if (!sizeKey) return null;
  const bySize = allPackaging.filter((p) => p?.size === sizeKey);
  if (!bySize.length) return null;
  return bySize.find((p) => p?.format === "Bulk") || bySize[0];
};

export default function BaseProductNameSection({ setProduct }) {
  const [rows, setRows] = useState([emptyRow()]);
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [bulkFormat, setBulkFormat] = useState("");
  const [generating, setGenerating] = useState(false);

  const [baseProducts, setBaseProducts] = useState([]);
  const [packagingVariants, setPackagingVariants] = useState([]);
  const bomCostCacheRef = React.useRef(new Map());

  const baseBySku = useMemo(() => {
    const map = new Map();
    baseProducts.forEach((p) => map.set(String(p?.sku || "").toUpperCase(), p));
    return map;
  }, [baseProducts]);

  const resolveBomCost = useCallback(async (sku) => {
    const key = String(sku || "").trim().toUpperCase();
    if (!key) return 0;
    if (bomCostCacheRef.current.has(key)) {
      return bomCostCacheRef.current.get(key);
    }

    const cost = await getBomEstimatedCost(key, {
      sku: key,
      onNotFound: (message) => toast.error(message, { duration: 8000 }),
    });
    const normalized = Number(cost) || 0;
    bomCostCacheRef.current.set(key, normalized);
    return normalized;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const hydrateMissingBomCosts = async () => {
      const rowsNeedingCost = rows.filter(
        (row) =>
          row?.baseSku &&
          (row?.bomCost === undefined ||
            row?.bomCost === null ||
            row?.bomCost === ""),
      );

      if (!rowsNeedingCost.length) return;

      const updates = await Promise.all(
        rows.map(async (row) => {
          if (
            !row?.baseSku ||
            (row?.bomCost !== undefined &&
              row?.bomCost !== null &&
              row?.bomCost !== "")
          ) {
            return row;
          }

          const bomCost = await resolveBomCost(row.baseSku);
          return { ...row, bomCost };
        }),
      );

      if (cancelled) return;
      setRows((prev) =>
        prev.map((row, idx) => {
          const nextRow = updates[idx];
          if (row.bomCost === nextRow.bomCost) return row;
          return { ...row, bomCost: nextRow.bomCost };
        }),
      );
    };

    hydrateMissingBomCosts();
    return () => {
      cancelled = true;
    };
  }, [rows, resolveBomCost]);

  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return baseProducts;
    return baseProducts.filter((p) => {
      const name = String(
        p?.customFields?.nameEn || p?.nameEn || "",
      ).toLowerCase();
      const sku = String(p?.sku || "").toLowerCase();
      return name.includes(q) || sku.includes(q);
    });
  }, [baseProducts, searchQuery]);

  const hydrateRowFromBase = (row, baseProduct) => {
    if (!baseProduct) return row;
    const baseCustomFields = baseProduct?.customFields || {};
    const csRow = (baseProduct?.barcodeSecondUom || []).find(
      (item) => String(item?.secondUom || "").toUpperCase() === "CS",
    );
    const masterDefaultWeight =
      Number(csRow?.quantityDefault) > 0 ? Number(csRow.quantityDefault) : 25;

    const next = {
      ...row,
      baseSku: baseProduct.sku || row.baseSku,
      baseCustomFields: {
        ingredientsEn:
          baseCustomFields.ingredients || baseCustomFields.ingredientsEn || "",
        ingredientsFr:
          baseCustomFields["ingrédients"] ||
          baseCustomFields.ingredientsFr ||
          "",
        allergensEn:
          baseCustomFields.allergens || baseCustomFields.allergensEn || "",
        allergensFr:
          baseCustomFields["allergènes"] ||
          baseCustomFields.allergensFr ||
          "",
        nameEn: baseCustomFields.nameEn || "",
        nameFr: baseCustomFields.nameFr || "",
        rawMaterialSku: baseCustomFields.rawMaterialSku || baseProduct.sku || "",
        qtyUn: baseCustomFields.qtyUn || "",
        un: baseCustomFields.un || "",
        netWeightGKg: baseCustomFields.netWeightGKg || "",
        gKg: baseCustomFields.gKg || "",
        netWeightOzLbs: baseCustomFields.netWeightOzLbs || "",
        fdaCode: baseCustomFields.fdaCode || "",
        fr: baseCustomFields.fr || "",
        categorie:
          baseCustomFields["catégorie"] ||
          baseCustomFields.categorie ||
          baseProduct.category ||
          "",
        labelCategory: baseCustomFields.labelCategory || "",
        expiryDays: baseCustomFields.expiryDays || baseProduct.expiryDays || "",
        new: baseCustomFields.new || "",
      },
      baseMsrpCad: Number(baseProduct?.msrpCad) || 0,
      baseMsrpUsd: Number(baseProduct?.msrpUsd) || 0,
      nameEn: String(
        baseProduct?.customFields?.nameEn || row.nameEn || "",
      ).toUpperCase(),
      nameFr: String(
        baseProduct?.customFields?.nameFr || row.nameFr || "",
      ).toUpperCase(),
      baseDescriptionEn: normalizeDescriptionRoot(
        baseProduct?.descriptionEn || baseProduct?.description || row.nameEn,
      ),
      baseDescriptionFr: normalizeDescriptionRoot(
        baseProduct?.descriptionFr ||
          baseProduct?.description ||
          row.nameFr ||
          row.nameEn,
      ),
      countryOfOrigin: baseProduct?.countryOfOrigin || "Canada",
      harmonizedTariff: baseProduct?.harmonizedTariff || "",
      fdaCode: baseProduct?.customFields?.fdaCode || "",
      expiryDays:
        baseProduct?.customFields?.expiryDays || baseProduct?.expiryDays || "",
      manualDate: baseProduct?.customFields?.manualDate || "",
      mfgCostPerKg: Number(baseProduct?.costing?.mfgCostPerKg) || 0,
      masterDefaultWeight,
    };

    if (next.isMasterCase && !normalizeWeight(next.actualWeight)) {
      next.actualWeight = masterDefaultWeight;
    }

    return next;
  };

  const recalcRow = (row, allPackaging = packagingVariants) => {
    const isMasterCase = row.formatType === "master_case";
    const defaultWeight = defaultWeightFromFormat(row.formatType);
    const effectiveWeight = isMasterCase
      ? normalizeWeight(row.actualWeight) ||
        Number(row.masterDefaultWeight || 25)
      : normalizeWeight(defaultWeight);

    const rowWithMaster = {
      ...row,
      isMasterCase,
      actualWeight: isMasterCase ? effectiveWeight : "",
    };

    const packagingChoice = rowWithMaster.selectedPackagingId
      ? allPackaging.find(
          (item) =>
            String(item._id) === String(rowWithMaster.selectedPackagingId),
        )
      : defaultPackagingForRow(rowWithMaster, allPackaging);

    const pkgCost = packagingCost(packagingChoice);
    const lengthMm =
      packagingChoice?.lengthMm ?? rowWithMaster.lengthMm ?? "";
    const widthMm = packagingChoice?.widthMm ?? rowWithMaster.widthMm ?? "";
    const heightMm =
      packagingChoice?.heightMm ?? rowWithMaster.heightMm ?? "";
    const lengthInch =
      packagingChoice?.lengthIn ?? rowWithMaster.lengthInch ?? "";
    const widthInch = packagingChoice?.widthIn ?? rowWithMaster.widthInch ?? "";
    const heightInch =
      packagingChoice?.heightIn ?? rowWithMaster.heightInch ?? "";
    const ti = packagingChoice?.ti ?? rowWithMaster.ti ?? "";
    const hi = packagingChoice?.hi ?? rowWithMaster.hi ?? "";
    const newFlag =
      rowWithMaster.new || rowWithMaster?.baseCustomFields?.new || "NEW";
    const descRootEn = rowWithMaster.baseDescriptionEn || rowWithMaster.nameEn;
    const descRootFr =
      rowWithMaster.baseDescriptionFr ||
      rowWithMaster.nameFr ||
      rowWithMaster.nameEn;

    const descriptionEn = isMasterCase
      ? `${descRootEn} 1 MASTER CS x ${effectiveWeight || rowWithMaster.masterDefaultWeight || 25} kg`.trim()
      : `${descRootEn} 1 CS x ${effectiveWeight || ""} kg`.trim();

    const descriptionFr = isMasterCase
      ? `${descRootFr} 1 MASTER CS x ${effectiveWeight || rowWithMaster.masterDefaultWeight || 25} kg`.trim()
      : `${descRootFr} 1 CS x ${effectiveWeight || ""} kg`.trim();

    return {
      ...rowWithMaster,
      selectedPackagingId: packagingChoice ? String(packagingChoice._id) : "",
      packagingName: packagingChoice?.name || "",
      packagingCost: Number(pkgCost.toFixed(4)),
      lineCost: Number(
        (
          (Number(rowWithMaster.mfgCostPerKg) || 0) *
            Number(effectiveWeight || 0) +
          pkgCost
        ).toFixed(4),
      ),
      lengthMm,
      widthMm,
      heightMm,
      lengthInch,
      widthInch,
      heightInch,
      ti,
      hi,
      new: newFlag,
      descriptionEn,
      descriptionFr,
      category: isMasterCase
        ? "Spices Bulk Master Case"
        : effectiveWeight
          ? `Spices Bulk ${effectiveWeight} KG`
          : "Spices Bulk Variant",
      packagingOptions: allPackaging,
    };
  };

  const getRowTotalCost = (row) => {
    const packagingCost = Number(row?.packagingCost) || 0;
    const weightKg =
      Number(row?.actualWeight) ||
      Number(defaultWeightFromFormat(row?.formatType)) ||
      0;
    const mfgCostPerKg = Number(row?.mfgCostPerKg) || 0;
    const bomCost = Number(row?.bomCost) || 0;
    const materialCostPerKg = mfgCostPerKg > 0 ? mfgCostPerKg : bomCost;
    const resolvedTotal =
      weightKg > 0 && materialCostPerKg > 0
        ? materialCostPerKg * weightKg + packagingCost
        : bomCost + packagingCost;
    return formatCostValue(resolvedTotal);
  };

  useEffect(() => {
    const loadInitial = async () => {
      try {
        const [productsRes, packagingRes] = await Promise.all([
          getAllSpiceBlends(),
          getAllPackagingVariants(),
        ]);

        const productList =
          productsRes?.products ||
          productsRes?.data?.products ||
          productsRes?.data ||
          [];
        const packagingList =
          packagingRes?.data || packagingRes?.packaging || packagingRes || [];

        setBaseProducts(Array.isArray(productList) ? productList : []);
        setPackagingVariants(Array.isArray(packagingList) ? packagingList : []);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load base products or packaging variants");
      }
    };

    loadInitial();
  }, []);

  useEffect(() => {
    setRows((prev) => prev.map((row) => recalcRow(row, packagingVariants)));
  }, [packagingVariants]);

  useEffect(() => {
    if (!setProduct) return;
    const variantsForSave = rows
      .filter((row) => row.generatedSku)
      .map((row) => ({
        sku: row.generatedSku,
        baseSku: row.baseSku,
        nameEn: row.nameEn,
        nameFr: row.nameFr,
        descriptionEn: row.descriptionEn,
        descriptionFr: row.descriptionFr,
        upc: row.upc,
        category: row.category,
        isMasterCase: row.isMasterCase,
        actualWeight:
          Number(row.actualWeight) ||
          Number(defaultWeightFromFormat(row.formatType)) ||
          0,
        netWeightKg:
          Number(row.actualWeight) ||
          Number(defaultWeightFromFormat(row.formatType)) ||
          0,
        lineCost: Number(row.lineCost) || 0,
        bomCost: Number(row.bomCost) || 0,
        packagingCost: Number(row.packagingCost) || 0,
        countryOfOrigin: row.countryOfOrigin,
        harmonizedTariff: row.harmonizedTariff,
        fdaCode: row.fdaCode,
        expiryDays: row.expiryDays,
        manualDate: row.manualDate,
        mfgCostPerKg: Number(row.mfgCostPerKg) || 0,
        lengthMm: row.lengthMm,
        widthMm: row.widthMm,
        heightMm: row.heightMm,
        lengthInch: row.lengthInch,
        widthInch: row.widthInch,
        heightInch: row.heightInch,
        ti: row.ti,
        hi: row.hi,
        new: row.new,
        baseCustomFields: row.baseCustomFields || {},
        baseMsrpCad: Number(row.baseMsrpCad) || 0,
        baseMsrpUsd: Number(row.baseMsrpUsd) || 0,
        packaging: row.selectedPackagingId
          ? [
              {
                packagingVariantId: row.selectedPackagingId,
                name: row.packagingName,
                format:
                  packagingVariants.find(
                    (p) => String(p._id) === String(row.selectedPackagingId),
                  )?.format || "",
                size:
                  packagingVariants.find(
                    (p) => String(p._id) === String(row.selectedPackagingId),
                  )?.size || "",
                items:
                  packagingVariants
                    .find(
                      (p) => String(p._id) === String(row.selectedPackagingId),
                    )
                    ?.items?.map((item) => ({
                      sku: item.sku,
                      name: item.name,
                      qty: Number(item.qty) || 0,
                      unitCost: Number(item.unitCost) || 0,
                    })) || [],
              },
            ]
          : [],
      }));

    setProduct((prev) => ({
      ...prev,
      nameEn: prev.nameEn || variantsForSave[0]?.nameEn || "",
      nameFr: prev.nameFr || variantsForSave[0]?.nameFr || "",
      baseProductSku: variantsForSave[0]?.baseSku || prev.baseProductSku || "",
      variantRowsForSave: variantsForSave,
      variantDraftHash: JSON.stringify(rows),
    }));
  }, [rows, setProduct, packagingVariants]);

  const addRowsFromSelected = () => {
    const selectedProducts = baseProducts.filter(
      (p) =>
        selectedIds.includes(String(p._id)) ||
        selectedIds.includes(String(p.sku)),
    );

    if (!selectedProducts.length) {
      setSelectedIds([]);
      setSearchQuery("");
      setShowModal(false);
      return;
    }

    const built = selectedProducts.map((baseProduct) => {
      const row = hydrateRowFromBase(
        {
          ...emptyRow(),
          baseSku: baseProduct.sku || "",
        },
        baseProduct,
      );
      return recalcRow(row, packagingVariants);
    });

    setRows((prev) => {
      const onlyEmpty =
        prev.length === 1 && !prev[0].baseSku && !prev[0].nameEn;
      return onlyEmpty ? built : [...prev, ...built];
    });

    setSelectedIds([]);
    setSearchQuery("");
    setShowModal(false);
  };

  const clearVariantRows = () => {
    setRows([emptyRow()]);
    setSelectedIds([]);
    setSearchQuery("");
    setBulkFormat("");
    setShowModal(false);
  };

  const handlePaste = (e, startIndex, field) => {
    e.preventDefault();
    const clipboardData = e.clipboardData.getData("text");
    const trimmedValues = clipboardData
      .split(/[\n\r\t,]+/)
      .map((val) => val.trim())
      .filter(Boolean);

    const isValidSku = (value) => /^\d{10}$/.test(value);
    let values = trimmedValues;

    if (field === "baseSku") {
      const invalid = trimmedValues.filter((val) => !isValidSku(val));
      values = trimmedValues.filter(isValidSku);
      if (invalid.length > 0) {
        toast.error(
          `Skipped ${invalid.length} invalid SKU${invalid.length > 1 ? "s" : ""}. SKUs must be numeric and exactly 10 digits.`,
        );
      }
    }

    if (!values.length) return;

    setRows((prev) => {
      const next = [...prev];

      for (let i = 0; i < values.length; i++) {
        const idx = startIndex + i;
        if (!next[idx]) next.push(emptyRow());

        if (field === "baseSku") {
          const baseSku = values[i].trim().toUpperCase();
          const base = baseBySku.get(baseSku);

          const merged = {
            ...next[idx],
            baseSku,
            generatedSku: "",
            upc: "",
            status: "",
            duplicateMessage: "",
          };

          next[idx] = recalcRow(
            hydrateRowFromBase(merged, base),
            packagingVariants,
          );
        }
      }

      return next;
    });
  };

  const updateRow = (index, patch) => {
    setRows((prev) => {
      const updated = [...prev];
      const merged = { ...updated[index], ...patch };
      const base = baseBySku.get(String(merged.baseSku || "").toUpperCase());
      updated[index] = recalcRow(
        hydrateRowFromBase(merged, base),
        packagingVariants,
      );
      return updated;
    });
  };

  const applyFormatToAll = (formatType) => {
    setRows((prev) =>
      prev.map((row) =>
        recalcRow(
          {
            ...row,
            formatType,
            generatedSku: "",
            upc: "",
            status: "",
            duplicateMessage: "",
          },
          packagingVariants,
        ),
      ),
    );
  };

  const generateAllSkus = async () => {
    const valid = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.baseSku && row.formatType && row.nameEn);

    if (!valid.length) {
      toast.error("Add base SKU and format before generation");
      return;
    }

    setGenerating(true);

    try {
      const payload = {
        type: "Regular",
        isBaseProduct: false,
        variants: valid.map(({ row, index }) => ({
          index,
          productNameEN: row.nameEn,
          productNameFR: row.nameFr,
          baseSku: row.baseSku,
          formatLabel: row.formatType,
          netWeightKg:
            Number(row.actualWeight) ||
            Number(defaultWeightFromFormat(row.formatType)) ||
            0,
          isMasterCase: row.formatType === "master_case",
          actualWeight:
            row.formatType === "master_case"
              ? Number(row.actualWeight)
              : undefined,
        })),
      };

      const response = await createRecipeSku(payload);
      if (!response?.success) {
        toast.error("Failed to generate variant SKUs");
        return;
      }

      setRows((prev) => {
        const next = [...prev];

        next.forEach((row, idx) => {
          next[idx] = { ...row, status: "", duplicateMessage: "" };
        });

        (response.variants || []).forEach((variant) => {
          const i = Number(variant.index);
          if (Number.isNaN(i) || !next[i]) return;

          const row = next[i];
          const packagingDetail = variant.packagingDetails;

          next[i] = recalcRow(
            {
              ...row,
              generatedSku: variant.sku || "",
              upc: variant.upc || "",
              descriptionEn: variant.descriptionEn || row.descriptionEn,
              descriptionFr: variant.descriptionFr || row.descriptionFr,
              category: variant.category || row.category,
              isMasterCase: Boolean(variant.isMasterCase),
              actualWeight: variant.actualWeight || row.actualWeight,
              countryOfOrigin:
                variant.copiedFields?.countryOfOrigin || row.countryOfOrigin,
              harmonizedTariff:
                variant.copiedFields?.harmonizedTariff || row.harmonizedTariff,
              fdaCode: variant.copiedFields?.fdaCode || row.fdaCode,
              expiryDays: variant.copiedFields?.expiryDays || row.expiryDays,
              manualDate: variant.copiedFields?.manualDate || row.manualDate,
              mfgCostPerKg:
                variant.copiedFields?.mfgCostPerKg || row.mfgCostPerKg,
              selectedPackagingId:
                packagingDetail?.boxId || row.selectedPackagingId,
              packagingName: packagingDetail?.name || row.packagingName,
              packagingCost: Number(
                packagingDetail?.cost || row.packagingCost || 0,
              ),
              lineCost: Number(variant?.lineCost || row.lineCost || 0),
              status: "success",
            },
            packagingVariants,
          );
        });

        (response.duplicateVariants || []).forEach((dup) => {
          const i = Number(dup.index);
          if (Number.isNaN(i) || !next[i]) return;
          next[i] = {
            ...next[i],
            status: "duplicate",
            duplicateMessage: dup.message || "Variant already exists",
          };
        });

        return next;
      });

      const hasSuccess = (response.variants || []).length > 0;
      const hasDuplicates = (response.duplicateVariants || []).length > 0;

      if (hasSuccess && hasDuplicates) {
        toast.success("Some variants generated successfully");
        toast.error("Some variants encountered errors");
      } else if (hasDuplicates && !hasSuccess) {
        const firstError = response.duplicateVariants[0]?.message || "Variant generation failed";
        toast.error(firstError);
      } else if (hasSuccess) {
        toast.success("All variants generated successfully");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Variant generation failed",
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border p-3 sm:p-6 shadow-sm min-w-0 w-full max-w-full overflow-hidden">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
        <h2 className="text-xl sm:text-2xl font-semibold">Variant Builder</h2>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          <button
            onClick={() => {
              setSelectedIds([]);
              setSearchQuery("");
              setShowModal(true);
            }}
            className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg"
          >
            <Plus size={16} /> Add Base Product
          </button>
          <button
            onClick={clearVariantRows}
            className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-100"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        {/* Header */}
        <div
          className="hidden md:grid text-xs font-semibold text-gray-700 bg-gray-100 p-3 rounded-t-lg border-b border-gray-300 sticky top-0 z-10"
          style={{
            gridTemplateColumns:
              "1.4fr 1.8fr 1.8fr 2fr 1.8fr 1fr 1fr 1fr 0.8fr",
            minWidth: 1280,
          }}
        >
          <div className="font-medium pl-1">Base SKU</div>
          <div className="font-medium">Description</div>
          <div className="font-medium flex items-center gap-2">
            <span>Format</span>
            <select
              value={bulkFormat}
              onChange={(e) => {
                setBulkFormat(e.target.value);
                if (e.target.value) applyFormatToAll(e.target.value);
              }}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white shadow-sm"
            >
              <option value="">Apply to all</option>
              {FORMAT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="font-medium">Packaging</div>
          <div className="font-medium">Variant Description</div>
          <div className="font-medium">Cost (BOM + Pkg)</div>
          <div className="font-medium">Variant SKU</div>
          <div className="font-medium">UPC</div>
          {/* <div className="font-medium ">Cost</div> */}
          <div className="font-medium text-center">Action</div>
        </div>

        {/* Rows */}
        <div className="space-y-3 md:space-y-0 md:divide-y md:divide-gray-200">
          {rows.map((row, i) => (
            <div
              key={row.id}
              className="border rounded-lg md:border-0 md:rounded-none p-4 md:p-0 bg-gray-50 md:bg-white"
            >
              <div
                className="hidden md:grid items-center gap-x-3 px-3 py-2.5"
                style={{
                  gridTemplateColumns:
                    "1.4fr 1.8fr 1.8fr 2fr 1.8fr 1fr 1fr 1fr 0.8fr",
                  minWidth: 1280,
                }}
              >
                {/* 1. Base SKU */}
                <input
                  value={row.baseSku}
                  onChange={(e) =>
                    updateRow(i, {
                      baseSku: e.target.value.trim().toUpperCase(),
                    })
                  }
                  onPaste={(e) => handlePaste(e, i, "baseSku")}
                  placeholder="Base SKU"
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm font-mono focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                />

                {/* 2. Description */}
                <input
                  value={row.nameEn}
                  readOnly
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-gray-50 cursor-default"
                />

                {/* 3. Format */}
                <select
                  value={row.formatType}
                  onChange={(e) =>
                    updateRow(i, {
                      formatType: e.target.value,
                      generatedSku: "",
                      upc: "",
                      status: "",
                      duplicateMessage: "",
                    })
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                >
                  <option value="">Choose format</option>
                  {FORMAT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>

                {/* 4. Packaging */}
                <select
                  value={row.selectedPackagingId}
                  onChange={(e) =>
                    updateRow(i, { selectedPackagingId: e.target.value })
                  }
                  disabled={!row.formatType}
                  className={`w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 ${
                    !row.formatType
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white"
                  }`}
                >
                  <option value="">No packaging</option>
                  {/* ... packaging options ... */}
                  {packagingVariants
                    .filter((pkg) => {
                      if (!row.formatType) return false;
                      const formatBulk = String(pkg?.format || "").toLowerCase() === "bulk";
                      if (!formatBulk) return false;
                      const expected =
                        row.formatType === "master_case"
                          ? "25kg"
                          : row.formatType;
                      return !pkg?.size || pkg.size === expected;
                    })
                    .map((pkg) => (
                      <option key={pkg._id} value={pkg._id}>
                        {pkg.name} ({pkg.size || "custom"})
                      </option>
                    ))}
                </select>

                {/* 5. Variant Description */}
                <input
                  value={row.descriptionEn}
                  readOnly
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-gray-50 cursor-default"
                />

                {/* 6. Cost (BOM + Packaging) */}
                <div className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm font-mono bg-gray-50 text-right">
                  {getRowTotalCost(row)}
                </div>

                {/* 7. Variant SKU */}
                <div className="w-full whitespace-nowrap border border-gray-300 rounded-md px-3 py-1.5 text-sm font-mono bg-gray-50">
                  {row.generatedSku || "auto generated"}
                </div>

                {/* 8. UPC */}
                <div className="w-full whitespace-nowrap border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-gray-50">
                  {row.upc || "auto generated"}
                </div>

                

                {/* 9. Cost */}
                {/* <div className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm font-medium bg-gray-50">
                  ${Number(row.lineCost ?? 0).toFixed(4)}
                </div> */}

                {/* 10. Action */}
                <div className="flex justify-center">
                  <button
                    onClick={() =>
                      setRows((prev) => prev.filter((r) => r.id !== row.id))
                    }
                    disabled={rows.length <= 1}
                    className="p-1 text-red-600 hover:text-red-800 disabled:opacity-40"
                    title="Remove row"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {/* Mobile fallback (minimal) */}
              <div className="md:hidden space-y-2 text-sm p-2">
                <div className="font-medium">{row.nameEn || "New variant"}</div>
                <div>Base: {row.baseSku || "—"}</div>
                <div>
                  Format:{" "}
                  {FORMAT_OPTIONS.find((f) => f.value === row.formatType)
                    ?.label || "—"}
                </div>
                <div>Cost: {getRowTotalCost(row) || "-"}</div>
                {row.duplicateMessage && (
                  <div className="text-red-600 text-xs">
                    {row.duplicateMessage}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Generate button can stay outside */}
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={generateAllSkus}
          // disabled={
          //   generating || !rows.some((row) => row.baseSku && row.formatType)
          // }
          disabled={
            generating
            
          }
          className={`px-5 py-2.5 rounded-lg font-medium ${
            generating
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-black hover:bg-gray-900 text-white"
          }`}
        >
          {generating ? "Generating..." : "Generate All SKUs"}
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center p-4 z-50">
          <div className="bg-white w-full max-w-3xl rounded-xl p-6">
            <h3 className="text-xl font-semibold mb-4">Select Base Products</h3>
            <input
              type="text"
              placeholder="Search by SKU or Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border rounded-lg px-4 py-2 mb-4"
            />

            <div className="max-h-[400px] overflow-y-auto space-y-2">
              {filteredProducts.length===0?(
                 <div className="flex flex-col items-center justify-center py-12 text-center">
      <p className="text-gray-500 font-medium">No products found</p>
      <p className="text-gray-400 text-sm mt-1">
        Try a different SKU or product name
      </p>
    </div>
              ):(
                <>
                  {filteredProducts.map((p) => {
                const id = String(p._id || p.sku);
                const name = p.customFields?.nameEn || p.nameEn || "Unnamed";
                return (
                  <label
                    key={id}
                    className="flex gap-3 border p-3 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(id)}
                      onChange={() => {
                        setSelectedIds((prev) =>
                          prev.includes(id)
                            ? prev.filter((item) => item !== id)
                            : [...prev, id],
                        );
                      }}
                    />
                    <div>
                      {name} - {p.sku}
                    </div>
                  </label>
                );
              })}
              </>

              )}
            
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setSelectedIds([]);
                  setSearchQuery("");
                  setShowModal(false);
                }}
              >
                Cancel
              </button>
              <button
                onClick={addRowsFromSelected}
                className="bg-black text-white px-4 py-2 rounded"
              >
                Add ({selectedIds.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
